import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import OptionsDropdownMenu, {
  DetailedViewOptions,
  OptionsDropdownSection
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import IconContainer from "@/components/ui/icon-container";
import { IconButtonSize } from "@/components/ui/icon-button";
import { ReportRowAction } from "@/components/global/Chat/components/dreamerReports/useReportRowActions";
import {
  ACTIONS_TRANSLATION_KEY,
  REPORT_ACTION_GROUPS
} from "@/components/global/Chat/components/dreamerReports/consts";

/** Sections exist only to draw the dividers the design shows, so they have no
 *  titles; the shared menu renders a label only for a section that has one. */
const SECTIONS: OptionsDropdownSection[] = REPORT_ACTION_GROUPS.map(
  (group) => ({ id: group })
);

interface ReportActionsMenuProps {
  actions: ReportRowAction[];
  /** A row reveals its trigger on hover; a view header always shows it. */
  revealOnRowHover?: boolean;
  size?: IconButtonSize;
}

/**
 * The three-dot menu on a report row. The shared menu is what handles the
 * closing sequence: an action that opens a dialog has to wait for the menu to
 * finish closing, since both manage pointer events on the document, and it
 * keeps focus off the trigger so a rename field is not blurred the moment it
 * appears — blurring commits the rename.
 *
 * The trigger is revealed on row hover, so every surface that renders this has
 * to mark its row `group`. Hiding it by opacity rather than unmounting keeps it
 * in the tab order.
 *
 * While the menu is open the pointer leaves the row for the menu, which is
 * portalled out of it, so the trigger is pinned visible by `aria-expanded`.
 * The shared menu's own open-state `opacity-100` cannot do this: it carries no
 * variant, so tailwind-merge keeps the media-scoped `opacity-0` beside it and
 * that rule wins the cascade. `data-state` cannot either: the tooltip trigger
 * wrapping this one overwrites it with the tooltip's state. The attribute
 * selector outranks the hiding rule on specificity, wherever each is emitted.
 *
 * The hidden state is scoped to `hover: hover`, because a device that cannot
 * hover can never reveal the trigger and the menu would be undiscoverable
 * there. Same approach as the row actions in `TermsHistoryEntry`.
 */
export const ReportActionsMenu: React.FC<ReportActionsMenuProps> = ({
  actions,
  revealOnRowHover = true,
  size = "lg"
}) => {
  const { t } = useTranslation();

  const options: DetailedViewOptions = useMemo(
    () =>
      actions.reduce<DetailedViewOptions>((collected, action) => {
        collected[action.key] = {
          action: action.onSelect,
          customText: action.label,
          customIcon: (
            <IconContainer
              size="5"
              color={
                action.disabled
                  ? "tertiary"
                  : action.isDestructive
                    ? "negative"
                    : "secondary"
              }
            >
              {action.icon}
            </IconContainer>
          ),
          variant: action.isDestructive ? "destructive" : "default",
          disabled: action.disabled,
          disabledTooltipText: action.tooltip,
          section: action.group
        };
        return collected;
      }, {}),
    [actions]
  );

  return (
    <OptionsDropdownMenu
      options={options}
      tPrefix={ACTIONS_TRANSLATION_KEY}
      buttonVariant="icon"
      className={
        revealOnRowHover
          ? "opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 aria-expanded:opacity-100 focus-visible:opacity-100"
          : undefined
      }
      iconSize={size}
      triggerIcon="horizontal-ellipsis"
      triggerDataTest="report-actions-menu"
      tooltipText={t(`${ACTIONS_TRANSLATION_KEY}.menu`)}
      contentClassName="min-w-[200px]"
      enableSections
      sections={SECTIONS}
      disableCloseAutoFocus
    />
  );
};
